// dmlRelationLists — the relation lists of an UPDATE / DELETE, read from its
// text: only the spans that ARE relation lists, never a SET assignment, an
// OUTPUT or a RETURNING list. `findReferencedRelationsForRange` re-arms on
// every top-level comma, so over a whole UPDATE it reads `SET status = 1,
// customers = 2` as naming a `customers` table.
//
//  - UPDATE: the target list before SET (MySQL `UPDATE a JOIN b ON … SET`,
//    `UPDATE a, b SET`), and a FROM list after SET (PostgreSQL / SQL Server /
//    SQLite `UPDATE … SET … FROM b`; `IS [NOT] DISTINCT FROM` is an operator).
//  - DELETE: the USING list when there is one (PostgreSQL, MySQL
//    `DELETE FROM a USING a JOIN b`), else the list after the first FROM
//    (`DELETE a FROM a JOIN b`, `DELETE a FROM a, b`) — and, past a SQL Server
//    OUTPUT clause or a second FROM (`DELETE FROM t FROM a JOIN b`), the FROM
//    list after it, the target before it a list of its own.
//
// A list ends at the statement's next clause (WHERE, RETURNING, OUTPUT,
// ORDER, LIMIT, OPTION). A list item is `name [AS] alias`; a derived table
// `(…) x` is skipped, and ON / USING (…) join conditions are not relations.
// Each list also comes back item by item (`ListedItem`: joined by a comma or
// a JOIN, and a JOIN's ON span) — what a subquery inside that ON may see
// (`onVisibleIndices`). Token-aware (quoting, comments, nesting), like the
// scanners it builds on.

import type { ParseOptions, QualifiedName } from '../../sql-grammar/model';
import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';
import { scanTopLevelKeywords, type TopKeyword } from '../structure/scanner';
import { isStructuralKeyword, skipParen } from './aliasScope.scanner';
import { foldIdentifier, isBareIdentStart, readIdentifier, readQualifiedName, type ReadIdentResult } from './identifiers';

export type ListedRelation = {
  readonly qualified: QualifiedName;
  /** `[AS] alias` after the name: as typed (undelimited), and folded by the resolver's rules. */
  readonly alias?: { readonly text: string; readonly folded: string };
  /** Where the name starts in the source. */
  readonly start: number;
};

/** One item of a relation list, in source order. */
export type ListedItem = {
  /** Where it starts: the relation's name, or a derived table's `(`. */
  readonly start: number;
  /** Hung off the item before it by a JOIN / APPLY — not after a comma or first in its list. */
  readonly joined: boolean;
  /** A joined item's ON condition, `[ON, end)`. */
  readonly on?: Span;
};

const LIST_END: ReadonlySet<string> = new Set(['WHERE', 'RETURNING', 'OUTPUT', 'ORDER', 'LIMIT', 'OPTION']);

/** Words that may sit where a relation is expected without being one (`UPDATE TOP (5) t`, `UPDATE OR IGNORE t`). */
const TARGET_MODIFIERS: ReadonlySet<string> = new Set([
  'TOP', 'ONLY', 'LOW_PRIORITY', 'IGNORE', 'QUICK', 'OR', 'REPLACE', 'ROLLBACK', 'ABORT', 'FAIL',
]);

/** Words after a relation that are not its alias (hints, clauses). */
const NOT_AN_ALIAS: ReadonlySet<string> = new Set([
  'OUTPUT', 'OPTION', 'USE', 'FORCE', 'PARTITION', 'TABLESAMPLE', 'IGNORE', 'INDEXED', 'NOT',
]);

export type Span = readonly [number, number];

export function dmlRelationLists(
  source: string,
  start: number,
  end: number,
  kind: 'update' | 'delete',
  options: ParseOptions,
): ListedRelation[] {
  return dmlRelationListing(source, start, end, kind, options).relations;
}

export type DmlRelationListing = {
  readonly relations: ListedRelation[];
  readonly derived: Span[];
  /** Each relation list's items, in source order. */
  readonly lists: ListedItem[][];
};

/**
 * The same lists, plus each derived table's `(…)` span (`[open, close)`) —
 * a relation-list item the structure may not carry (MySQL `UPDATE a JOIN
 * (SELECT …) x`), which sees none of its sibling items.
 */
export function dmlRelationListing(
  source: string,
  start: number,
  end: number,
  kind: 'update' | 'delete',
  options: ParseOptions,
): DmlRelationListing {
  const words = scanTopLevelKeywords(source, start, end, options);
  const ctx = skipContextFor(options);
  const spans = kind === 'update' ? updateSpans(words, end) : deleteSpans(source, words, end, ctx);
  const lists = spans.map(([from, to]) => readRelationList(source, from, to, options, ctx));
  return {
    relations: lists.flatMap((l) => l.relations),
    derived: lists.flatMap((l) => l.derived),
    lists: lists.map((l) => l.items),
  };
}

const indexAfter = (words: readonly TopKeyword[], after: number, test: (w: TopKeyword) => boolean): number =>
  words.findIndex((w, i) => i > after && test(w));

/** A FROM that starts a relation list — not `IS [NOT] DISTINCT FROM`. */
const listFrom = (words: readonly TopKeyword[], after: number, before = words.length): number => {
  const at = indexAfter(words, after, (w) => w.upper === 'FROM');
  if (at < 0 || at >= before) return -1;
  return words[at - 1]?.upper === 'DISTINCT' ? listFrom(words, at, before) : at;
};

function updateSpans(words: readonly TopKeyword[], end: number): Span[] {
  const update = words.findIndex((w) => w.upper === 'UPDATE');
  if (update < 0) return [];
  const set = indexAfter(words, update, (w) => w.upper === 'SET');
  if (set < 0) return [[words[update]!.end, end]];
  const spans: Span[] = [[words[update]!.end, words[set]!.start]];
  // The FROM list comes before WHERE (SQL Server may put OUTPUT ahead of it).
  const bound = indexAfter(words, set, (w) => w.upper !== 'OUTPUT' && LIST_END.has(w.upper));
  const from = listFrom(words, set, bound < 0 ? words.length : bound);
  if (from >= 0) spans.push([words[from]!.end, listStop(words, from, end).to]);
  return spans;
}

function deleteSpans(source: string, words: readonly TopKeyword[], end: number, ctx: SkipContext): Span[] {
  const spans: Span[] = [];
  let at = words.findIndex((w) => w.upper === 'DELETE');
  while (at >= 0) {
    const from = listFrom(words, at);
    if (from < 0) break;
    const stop = listStop(words, from, end);
    // SQL Server `DELETE [FROM] t FROM <list>`: the target, then the list.
    const bound = indexAfter(words, from, (w) => w.start >= stop.to);
    const second = listFrom(words, from, bound < 0 ? words.length : bound);
    // A list-level USING (not a join's `USING (col)`, whatever comments sit
    // before its paren) starts the relation list.
    const using = words.find(
      (w, i) => i > from && w.start < stop.to && w.upper === 'USING' && source[skipTrivia(source, w.end, stop.to, ctx)] !== '(',
    );
    if (second >= 0) spans.push([words[from]!.end, words[second]!.start], [words[second]!.end, stop.to]);
    else spans.push([using?.end ?? words[from]!.end, stop.to]);
    at = stop.output;
  }
  return spans;
}

/** Where the list starting at `words[from]` ends, and the OUTPUT keyword's index when that is what ends it (else -1). */
function listStop(words: readonly TopKeyword[], from: number, end: number): { readonly to: number; readonly output: number } {
  const stop = indexAfter(words, from, (w) => LIST_END.has(w.upper));
  if (stop < 0) return { to: end, output: -1 };
  return { to: words[stop]!.start, output: words[stop]!.upper === 'OUTPUT' ? stop : -1 };
}

// eslint-disable-next-line complexity
function readRelationList(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
  ctx: SkipContext,
): { readonly relations: ListedRelation[]; readonly derived: Span[]; readonly items: ListedItem[] } {
  const out: ListedRelation[] = [];
  const derived: Span[] = [];
  const items: ListedItem[] = [];
  let expect = true;
  let joined = false;
  let topArgument = false;
  // The open ON condition: where it starts, and the index of the item it belongs to.
  let on: { readonly start: number; readonly item: number } | null = null;
  const closeOn = (at: number): void => {
    const item = on ? items[on.item] : undefined;
    if (on && item) items[on.item] = { ...item, on: [on.start, at] };
    on = null;
  };
  let i = start;
  while (i < end) {
    const ident = identAt(source, i, end, options);
    if (ident) {
      const upper = ident.quoted ? null : ident.value.toUpperCase();
      topArgument = upper === 'TOP';
      if (upper === 'JOIN' || upper === 'STRAIGHT_JOIN' || upper === 'APPLY') {
        closeOn(i);
        expect = true;
        joined = true;
      } else if (upper === 'ON' || upper === 'USING') {
        expect = false;
        if (upper === 'ON' && items.length > 0) on = { start: i, item: items.length - 1 };
      } else if (expect && !(upper !== null && (TARGET_MODIFIERS.has(upper) || isStructuralKeyword(upper)))) {
        const read = readQualifiedName(source, i, end, options);
        if (read) {
          const alias = readAlias(source, read.range.endOffset, end, options, ctx);
          out.push({ qualified: read.qualified, ...(alias ? { alias: alias.alias } : {}), start: i });
          items.push({ start: i, joined });
          expect = false;
          i = alias?.end ?? read.range.endOffset;
          continue;
        }
      }
      i = ident.end;
      continue;
    }
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    if (source[i] === '(') {
      // `TOP (n)`'s argument, else a derived table (no snapshot columns) or a
      // join condition / hint list.
      const close = skipParen(source, i, end, ctx);
      if (expect && !topArgument) {
        derived.push([i, close]);
        items.push({ start: i, joined });
      }
      i = close;
      expect = expect && topArgument;
      topArgument = false;
      continue;
    }
    if (source[i] === ',') {
      closeOn(i);
      expect = true;
      joined = false;
    }
    i++;
  }
  closeOn(end);
  return { relations: out, derived, items };
}

function readAlias(
  source: string,
  from: number,
  end: number,
  options: ParseOptions,
  ctx: SkipContext,
): { readonly alias: NonNullable<ListedRelation['alias']>; readonly end: number } | null {
  let ident = identAt(source, skipTrivia(source, from, end, ctx), end, options);
  if (ident && !ident.quoted && ident.value.toUpperCase() === 'AS') {
    ident = identAt(source, skipTrivia(source, ident.end, end, ctx), end, options);
  } else if (ident && !ident.quoted) {
    const upper = ident.value.toUpperCase();
    if (isStructuralKeyword(upper) || NOT_AN_ALIAS.has(upper)) return null;
  }
  if (!ident) return null;
  return { alias: { text: ident.value, folded: foldIdentifier(ident.value, ident.quoted, options) }, end: ident.end };
}

function identAt(source: string, at: number, end: number, options: ParseOptions): ReadIdentResult | null {
  const ident = readIdentifier(source, at, end, options);
  return ident && (ident.quoted || isBareIdentStart(source, at, options.engine)) ? ident : null;
}

/** Past whitespace and comments (never past a quoted identifier, which `trySkipNonCode` would also skip). */
export function skipTrivia(source: string, from: number, end: number, ctx: SkipContext): number {
  let i = from;
  while (i < end) {
    if (/\s/.test(source[i]!)) { i++; continue; }
    const comment =
      source.startsWith('--', i) || source.startsWith('/*', i) || (ctx.supportsHashComment && source[i] === '#');
    if (!comment) break;
    i = trySkipNonCode(source, i, end, ctx);
  }
  return i;
}
